"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  CircularProgress,
  Box,
  Stack,
} from "@mui/material";
import Logo from "../Assets/company-logo.svg";
import { styled } from "@mui/system";
import Papa from "papaparse";
import confetti from "canvas-confetti";
import { getRandomDarkPastelColor } from "../utils/get-random-color";
import "./SpinWheelDraw.css";
import Modal from "@mui/material/Modal";

const WheelContainer = styled("div")(({ theme }) => ({
  position: "relative",
  width: 400,
  height: 400,
  borderRadius: "50%",
  border: "none",
  overflow: "hidden",
  margin: "auto",
}));

const WheelCanvas = styled("canvas")({
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  border: "none",
});

export default function SpinWheelDraw() {
  const [entries, setEntries] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [selected, setSelected] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const wheelRef = useRef();
  const canvasRef = useRef();

  // CSV upload handler (keep for data input, but hide UI)
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = results.data
          .map((row) => ({
            firstName: row.firstName || row["First Name"] || row["first name"],
            lastName: row.lastName || row["Last Name"] || row["last name"],
            phone: row.phone || row["Phone"] || row["phone"],
          }))
          .filter((row) => row.firstName && row.lastName && row.phone);
        setEntries(parsed);
      },
    });
  };

  const drawWheel = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const size = canvas.width;
    const radius = size / 2;
    const angleStep =
      entries.length > 0 ? (2 * Math.PI) / entries.length : 2 * Math.PI;
    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.translate(radius, radius);
    if (entries.length === 0) {
      ctx.beginPath();
      ctx.arc(0, 0, radius, 0, 2 * Math.PI);
      ctx.fillStyle = "#f4f4f4";
      ctx.fill();
      ctx.restore();
      return;
    }
    entries.forEach((entry, i) => {
      const startAngle = i * angleStep;
      const endAngle = startAngle + angleStep;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius, startAngle, endAngle);
      ctx.fillStyle = getRandomDarkPastelColor();
      ctx.fill();
      // Draw text as before, near center
      ctx.save();
      ctx.rotate(startAngle + angleStep / 2);
      ctx.textAlign = "right";
      ctx.fillStyle = "white";
      ctx.font = "12px sans-serif";
      const displayName = entry.firstName
        ? `${entry.firstName} ${entry.lastName}`
        : entry.name;
      ctx.fillText(displayName, radius - 30, 10);
      ctx.restore();
    });
    ctx.restore();
  }, [entries]);

  useEffect(() => {
    drawWheel();
  }, [entries, drawWheel]);

  const handleSpin = () => {
    if (entries.length === 0 || isSpinning) return;
    setIsSpinning(true);
    setCountdown(5);
    // Start spinning immediately
    const selectedIndex = Math.floor(Math.random() * entries.length);
    const degrees = 3600 + (360 / entries.length) * selectedIndex;
    wheelRef.current.style.transition = "transform 5s ease-out";
    wheelRef.current.style.transform = `rotate(-${degrees}deg)`;
    // Reveal winner after 5 seconds
    setTimeout(() => {
      const selectedEntry = entries[selectedIndex];
      setSelected(selectedEntry);
      setShowModal(true);
      setIsSpinning(false);
      wheelRef.current.style.transition = "none";
      wheelRef.current.style.transform = `rotate(0deg)`;
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
    }, 5000);
  };

  // Countdown logic
  useEffect(() => {
    if (isSpinning && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [isSpinning, countdown]);

  return (
    <>
      <Box
        className="nav-logo-container"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          transition: "top 0.5s",
        }}
      >
        <a href="/">
          <img
            className="company-logo"
            src={Logo}
            alt="company-logo"
            width={100}
          />
        </a>
      </Box>
      <Box
        className="spinwheel-container"
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ textAlign: "center", mt: 2, mx: "auto" }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: "bold" }}>
            LUCKY DRAW
          </Typography>
          <input
            type="file"
            accept=".csv"
            onChange={handleCSVUpload}
            style={{ marginBottom: 32 }}
          />
        </Box>

        <Box
          sx={{
            position: "relative",
            height: { xs: "80vw" },
            width: "auto",
            maxWidth: "auto",
            maxHeight: "80vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <div
            ref={wheelRef}
            style={{
              width: "100%",
              height: "100%",
              transformOrigin: "center center",
            }}
          >
            <WheelCanvas ref={canvasRef} width={600} height={600} />
          </div>
          <Button
            variant="contained"
            onClick={handleSpin}
            disabled={entries.length === 0}
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: 14,
              p: 2,
              borderRadius: 8,
              backgroundColor: "#00ccbb",
              opacity: 1,
              transition: "background 0.2s",
              "&:hover": {
                backgroundColor: "#00b3a6", // Similar to button color, slightly darker
              },
            }}
          >
            {isSpinning ? (
              countdown > 0 ? (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: 18,
                  }}
                >
                  {countdown}
                </span>
              ) : (
                <CircularProgress size={24} color="inherit" />
              )
            ) : (
              "SPIN"
            )}
          </Button>
        </Box>
        <Modal open={showModal} onClose={() => setShowModal(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              px: 6,
              py: 4,
              borderRadius: 4,
              textAlign: "center",
              opacity: 1,
            }}
          >
            <Typography variant="h4" color="#005645" gutterBottom>
              Winner!
            </Typography>
            {selected && (
              <>
                <Typography variant="h5">
                  {selected.firstName} {selected.lastName}
                </Typography>
                <Typography variant="body1">Phone: {selected.phone}</Typography>
              </>
            )}
            <Button
              variant="contained"
              sx={{
                mt: 4,
                backgroundColor: "#00ccbb",
                transition: "background 0.2s",
                "&:hover": {
                  backgroundColor: "#00b3a6",
                },
              }}
              onClick={() => setShowModal(false)}
            >
              Close
            </Button>
          </Box>
        </Modal>
      </Box>
    </>
  );
}
